"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { TrendingUp, CircleCheck as CheckCircle2, ArrowRight, ChartBar as BarChart3, Target, Zap, ShieldCheck, Award, Sparkles, UserCheck, ChevronRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const caseStudies = [{
  id: "logistics",
  title: "Midlands Freight & Logistics",
  subtitle: "Local SEO & Google Business Profile Optimization",
  metricValue: "+280%",
  metricLabel: "Organic Local Traffic",
  challenge: "Struggling to rank locally against national aggregators with zero inbound lead flow from Google Maps across core Midlands service areas.",
  strategy: "Geo-targeted search architecture, Google Business Profile optimization, localized schema markup, and authoritative local citation cleanup.",
  implementation: "Corrected NAP inconsistencies, launched dedicated hyper-local service hubs, optimized GMB category signals, and earned high-tier UK industry backlinks.",
  verifiedResults: "Achieved top 3 Google positions for 14 core local keywords and increased organic local search traffic by 280% within 5 months.",
  businessImpact: "Secured over £140,000 in verified new logistics contracts directly attributed to organic search leads in the first half-year.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_logistics.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_logistics.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_logistics.png-wvc-sizes",
  imageAlt: "Midlands Freight Logistics organic growth chart and local search results",
  category: "Local SEO"
}, {
  id: "saas",
  title: "Enterprise Workflow UK",
  subtitle: "National B2B Search Architecture & Lead Generation",
  metricValue: "+340%",
  metricLabel: "Qualified B2B Demo Requests",
  challenge: "Stagnant organic traffic pipeline and zero non-brand search visibility in a fiercely competitive UK enterprise software sector.",
  strategy: "High-intent solution page re-architecture, technical crawl efficiency fixes, and comprehensive B2B search content hubs.",
  implementation: "Restructured site taxonomy, resolved duplicate indexation issues, crafted buyer-intent landing pages, and optimized demo funnel conversion rates.",
  verifiedResults: "Expanded top 3 Google keyword rankings by 65% and drove a 340% surge in inbound qualified enterprise demo bookings.",
  businessImpact: "Reduced cost per organic acquisition by 52%, creating a sustainable, predictable customer acquisition engine.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_saas.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_saas.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_saas.webp-wvc-sizes",
  imageAlt: "Enterprise Workflow UK B2B search dashboard showing demo request growth",
  category: "B2B & National SEO"
}, {
  id: "retail",
  title: "Craft & Timber Supplies",
  subtitle: "Ecommerce Technical SEO & Faceted Indexing",
  metricValue: "12.4x",
  metricLabel: "Return on SEO Investment",
  challenge: "Faceted navigation indexation bloat causing severe keyword cannibalization and falling product revenue behind major national retailers.",
  strategy: "Faceted search indexing consolidation, eCommerce product schema enrichment, and intent-driven category optimization.",
  implementation: "Eliminated indexation crawl traps, configured precise canonical controls, optimized high-margin product category copy, and increased page speed performance.",
  verifiedResults: "Achieved +195% organic revenue growth year-over-year alongside a +220% increase in non-brand search impressions.",
  businessImpact: "Delivered a verified 12.4x return on consultancy fees with compounding month-on-month organic store sales.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_ecommerce.png-wvc-sizes",
  imageAlt: "Craft and Timber Supplies ecommerce sales analytics and organic growth",
  category: "Ecommerce SEO"
}];
const trustPoints = [{
  id: 1,
  title: "Direct Consultant Access",
  desc: "You work directly with me — Yassine Essaadi. No account managers, delegates, or junior agency staff."
}, {
  id: 2,
  title: "Tailored SEO Strategy",
  desc: "Bespoke campaigns engineered around your specific market, geography, and commercial revenue targets."
}, {
  id: 3,
  title: "Verified Revenue Impact",
  desc: "Honest, transparent measurement tied to phone calls, leads, and real sales — never vanity metrics."
}];
export default function CaseStudy() {
  const [activeTab, setActiveTab] = useState<string>("logistics");
  const shouldReduceMotion = useReducedMotion();
  const selectedStudy = caseStudies.find(study => study.id === activeTab) || caseStudies[0];
  return <section id="casestudyimpact" data-nav="light" className="bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(var(--color-primary)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Award className="size-3.5 text-primary" />
            Verified SEO Case Studies
          </Badge>
          
          <h2 className="font-serif text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Real SEO Growth. Proven Business Impact.
          </h2>
          
          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Explore how my hands-on SEO consultancy has delivered measurable revenue, market position, and sustainable traffic growth for businesses across the UK.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {caseStudies.map((study, i) => {
          const isActive = study.id === activeTab;
          return <div data-index={i} key={study.id}>
                <Button variant={isActive ? "default" : "outline"} onClick={() => setActiveTab(study.id)} className={`h-auto px-5 py-3 text-sm font-medium transition-[color,background-color,border-color,box-shadow] duration-200 ${isActive ? "bg-primary text-primary-foreground shadow-md" : "bg-card text-card-foreground hover:bg-muted"}`}>
                  <span className="flex items-center gap-2">
                    <span className="font-semibold">{study.title}</span>
                    <Badge variant="outline" className={`hidden sm:inline-flex text-xs ${isActive ? "border-primary-foreground/30 text-primary-foreground" : "text-muted-foreground"}`}>
                      {study.category}
                    </Badge>
                  </span>
                </Button>
              </div>;
        })}
        </div>

        <div className="mt-12">
          <AnimatePresence mode="wait">
            <motion.div key={selectedStudy.id} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={shouldReduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -16
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border-border/60 bg-card shadow-xl transition-all duration-300">
                <CardHeader className="border-b border-border/40 pb-8 pt-8">
                  <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                          {selectedStudy.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">• Verified Client Project</span>
                      </div>
                      <CardTitle className="mt-3 font-serif text-2xl font-bold sm:text-3xl text-card-foreground">
                        {selectedStudy.title}
                      </CardTitle>
                      <CardDescription className="mt-1 text-base text-muted-foreground">
                        {selectedStudy.subtitle}
                      </CardDescription>
                    </div>

                    <div className="flex items-center gap-4 rounded-xl bg-secondary/50 p-4 border border-secondary sm:min-w-[240px]">
                      <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
                        <TrendingUp className="size-6" />
                      </div>
                      <div>
                        <div className="font-serif text-3xl font-bold text-primary">
                          {selectedStudy.metricValue}
                        </div>
                        <div className="text-xs font-medium text-foreground/80">
                          {selectedStudy.metricLabel}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-6 sm:p-8 lg:p-10">
                  <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
                    
                    <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
                      <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-md">
                        <img src={selectedStudy.imageSrc} data-wvc-srcset={selectedStudy.imageSrcset} data-wvc-sizes={selectedStudy.imageSizes} alt={selectedStudy.imageAlt} loading="lazy" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                      </div>

                      <div className="rounded-xl border border-border/60 bg-muted/40 p-5">
                        <div className="flex items-start gap-3">
                          <SafeHydrate><Avatar className="size-10 border border-primary/20">
                            <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_profile.png" alt="Yassine Essaadi" />
                            <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                              YE
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div className="text-xs">
                            <div className="font-semibold text-foreground flex items-center gap-1">
                              <span>Yassine Essaadi</span>
                              <Sparkles className="size-3 text-accent" />
                            </div>
                            <div className="text-muted-foreground">Freelance SEO Consultant</div>
                            <p className="mt-2 text-foreground/90 italic">
                              {"\"I personally executed every phase of this project. When you work with YE Consultancy, you work directly with me — no agency fluff.\""}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-7 space-y-6">
                      
                      <div className="rounded-lg bg-background p-5 border border-border/50 shadow-xs">
                        <div className="flex items-center gap-2.5 font-serif font-semibold text-foreground">
                          <div className="flex size-7 items-center justify-center rounded-md bg-destructive/10 text-destructive text-xs font-bold">
                            1
                          </div>
                          <span className="text-base text-foreground">The Challenge</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground pl-9">
                          {selectedStudy.challenge}
                        </p>
                      </div>

                      <div className="rounded-lg bg-background p-5 border border-border/50 shadow-xs">
                        <div className="flex items-center gap-2.5 font-serif font-semibold text-foreground">
                          <div className="flex size-7 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold">
                            2
                          </div>
                          <span className="text-base text-foreground">The Strategy</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground pl-9">
                          {selectedStudy.strategy}
                        </p>
                      </div>

                      <div className="rounded-lg bg-background p-5 border border-border/50 shadow-xs">
                        <div className="flex items-center gap-2.5 font-serif font-semibold text-foreground">
                          <div className="flex size-7 items-center justify-center rounded-md bg-chart-4/10 text-chart-4 text-xs font-bold">
                            3
                          </div>
                          <span className="text-base text-foreground">Implementation</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground pl-9">
                          {selectedStudy.implementation}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div className="rounded-lg bg-secondary/40 p-5 border border-secondary">
                          <div className="flex items-center gap-2 font-serif text-sm font-bold text-foreground">
                            <CheckCircle2 className="size-4 text-primary" />
                            <span>Verified Results</span>
                          </div>
                          <p className="mt-2 text-xs leading-relaxed text-foreground/90 font-medium">
                            {selectedStudy.verifiedResults}
                          </p>
                        </div>

                        <div className="rounded-lg bg-primary/10 p-5 border border-primary/20">
                          <div className="flex items-center gap-2 font-serif text-sm font-bold text-primary">
                            <Zap className="size-4 text-primary" />
                            <span>Business Impact</span>
                          </div>
                          <p className="mt-2 text-xs leading-relaxed text-foreground font-semibold">
                            {selectedStudy.businessImpact}
                          </p>
                        </div>
                      </div>

                    </div>

                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-16 rounded-2xl bg-muted/50 p-8 border border-border/60">
          <div className="mb-6 text-center lg:text-left">
            <h3 className="font-serif text-xl font-bold text-foreground">
              Why UK Businesses Choose Direct Consultant Access
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Transparent, accountable, and strategy-led search consultancy designed for real return on investment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustPoints.map((point, i) => <div data-index={i} key={point.id} className="rounded-xl bg-background p-5 border border-border/40 shadow-xs">
                <div className="flex items-center gap-3">
                  <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    {point.id === 1 && <UserCheck className="size-5" />}
                    {point.id === 2 && <Target className="size-5" />}
                    {point.id === 3 && <BarChart3 className="size-5" />}
                  </div>
                  <h4 className="font-serif font-semibold text-sm text-foreground">
                    {point.title}
                  </h4>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                  {point.desc}
                </p>
              </div>)}
          </div>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-2xl bg-primary p-8 text-primary-foreground shadow-lg">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-serif text-xl font-bold text-primary-foreground sm:text-2xl">
              Ready to achieve similar organic growth for your business?
            </h3>
            <p className="text-sm text-primary-foreground/90 max-w-xl">
              Book a free 30-minute SEO consultation directly with me. We will examine your current search visibility and outline clear growth opportunities.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
            <Button asChild size="lg" className="bg-background text-foreground hover:bg-background/90 shadow-md transition-all font-semibold">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ChevronRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
              <Link to="/CaseStudiesPage">
                <span>Explore All Case Studies</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}